import { useMemo } from 'react';
import { t } from 'i18next';
import { PhoneInput } from '@Components/inputs/Phone';
import { OnboardingFormFields } from '@Types/types.ts';
import { useBoundStore } from '@Store/useBoundStore.ts';
import { useGetCountries } from '@Hooks/api/useGetCountries';
import type { Dispatch, SetStateAction } from 'react';
import type { IPhoneForm } from '@Pages/happyFlowPages/phone/phoneForm/phoneForm.types.ts';
import type { ExtendedUseFormReturn } from '@Hooks/index';

import type { RegionCode } from 'google-libphonenumber';

interface IPhoneFormProps {
  formMethods: ExtendedUseFormReturn<IPhoneForm>;
  isSelectMenuDisabled: boolean;
  setLocalSelectedIsoCode?: Dispatch<SetStateAction<RegionCode>>;
}

export const PhoneForm = (props: IPhoneFormProps) => {
  const { phoneCountryCode } = useBoundStore((state) => state.customer.customerData);
  const { data: countriesDataResponse } = useGetCountries(true);
  const {
    isSelectMenuDisabled,
    setLocalSelectedIsoCode,
    formMethods: { getValues, getFieldState, setValue, setError, clearErrors },
  } = props;

  const handlePhoneValidationStatus = (isValid: boolean) => {
    setValue(OnboardingFormFields.PHONE_NUMBER_VALIDATION_STATUS, isValid);

    if (isValid) {
      // The resolver adds the phone error on PHONE_NUMBER when the status is
      // false on change, so clear it once the number becomes valid.
      clearErrors(OnboardingFormFields.PHONE_NUMBER);
    } else if (getFieldState(OnboardingFormFields.PHONE_NUMBER).isTouched) {
      setError(OnboardingFormFields.PHONE_NUMBER, { message: t('enErrors:phone') });
    }
  };

  const handleIsoCodeChange = (isoCode: RegionCode) => {
    setLocalSelectedIsoCode?.(isoCode);
  };

  const handleBlur = () => {
    // Re-check validity when the timeout fires: the number may become valid
    // between the blur and the deferred setError, which would otherwise
    // re-add the error after it was cleared on change.
    setTimeout(() => {
      if (
        !getValues(OnboardingFormFields.PHONE_NUMBER_VALIDATION_STATUS) &&
        getFieldState(OnboardingFormFields.PHONE_NUMBER).isTouched
      ) {
        setError(OnboardingFormFields.PHONE_NUMBER, { message: t('enErrors:phone') });
      }
    }, 100);
  };

  const countriesFilterList = useMemo(() => {
    if (!countriesDataResponse) return ['US'];
    return countriesDataResponse.countries.map((country) => country.code);
  }, [countriesDataResponse]);

  return (
    <PhoneInput
      onIsoCodeChange={handleIsoCodeChange}
      initialIsoCode={phoneCountryCode}
      isSelectMenuDisabled={isSelectMenuDisabled}
      onBlur={handleBlur}
      onPhoneValidationStatusChange={handlePhoneValidationStatus}
      filteredCountries={countriesFilterList as RegionCode[]}
    />
  );
};
